import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router } from '@angular/router';
import { Observable, filter, first, of, switchMap } from 'rxjs';
import { UserDetails } from '@bn/api-interfaces';
import { UserDetailsStateService } from '@bn/auth/services';
import { ApplicationRole } from '@bn/shared/entities/enums';

/**
 *
 * Usage examples:
 *
 * Routing item example:
 *
 * {
 *    path: AppRoutes.Script,
 *    component: Scripts,
 *    title: 'Scripts',
 *    canActivate: [UserPermissionsGuard],
 *    data: {
 *      roleIds: ['SubuserManage']
 *    }
 *  }
 *
 */

@Injectable({ providedIn: 'root' })
export class UserPermissionsGuard implements CanActivate {
  private readonly userDetailsService = inject(UserDetailsStateService);
  private readonly router = inject(Router);

  canActivate(route: ActivatedRouteSnapshot): Observable<boolean> {
    return this.userDetailsService.getUserDetails().pipe(
      filter((userDetails: UserDetails | null) => userDetails !== null),
      switchMap((userDetails: UserDetails | null) => {
        const roleIds = route.data['roleIds'] as ApplicationRole[];
        const roles = userDetails?.Roles || [];
        const foundAny = roleIds?.some((r) => roles.indexOf(r) >= 0);

        if (foundAny || (roles.includes('User') && !roleIds?.includes('Agent'))) return of(true);

        this.router.navigate(['/errors/unauthorized']);

        return of(false);
      }),
      first(),
    );
  }
}
